import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { onboardingApi } from '../utils/api';
import { CheckIcon, CircleIcon, CircleCheckIcon } from './icons/LucideIcons';
import PageHeader from './PageHeader';
import { LINK_INK } from './orderListFormat';

/**
 * Getting Started — the tenant onboarding funnel (Bouqmark Phase 2, slice 2;
 * real completion since audit P2 #17).
 *
 * A new shop's setup checklist. Steps come from the backend funnel API and
 * complete themselves from the shop's real state (services/onboardingState.js):
 * each item links to the exact screen where it is set, and a step is done
 * once all its items are. Optional steps can be skipped. Tenants provisioned
 * before the funnel existed (Twigs) get applicable=false and never see this
 * page's nav entry.
 */

// Where each checklist item is set: the exact screen, and the card on it.
export const ITEM_LINKS = {
  store_address: { to: '/settings?tab=store-hours#store-address', screen: 'Store info' },
  store_hours: { to: '/settings?tab=store-hours', screen: 'Store info' },
  timezone: { to: '/settings?tab=general#store-timezone', screen: 'General settings' },
  order_number_prefix: { to: '/settings?tab=general#order-numbers', screen: 'General settings' },
  sales_tax_rate: { to: '/settings?tab=general', screen: 'General settings' },
  products: { to: '/store/products', screen: 'Products' },
  staff_login: { to: '/settings?tab=staff', screen: 'Staff settings' },
  delivery_zones: { to: '/settings?tab=delivery', screen: 'Delivery settings' },
  print_client: { to: '/settings?tab=print-logs#print-client', screen: 'Print logs' },
  texting: { to: '/settings?tab=customer-texts#texting-registration', screen: 'Customer texts' },
  first_order: { to: '/create-order', screen: 'Create an order' },
  card_payments: { to: '/settings?tab=payments', screen: 'Payments' },
  store_published: { to: '/store', screen: 'Online store' },
  plan_chosen: { to: '/settings?tab=plan&choose=1', screen: 'Plan & billing' },
};

// Step status tags: StatusTag's Forest geometry and tones (done = ok with a
// check, skipped = neutral, still to do = warn with a dot).
const TAG = 'inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold capitalize';
const STATUS_STYLES = {
  completed: 'tag-check bg-ok-bg text-ok-fg',
  skipped: 'bg-neutral-bg text-neutral-fg',
  pending: 'tag-dot bg-warn-bg text-warn-fg',
};

// The step's main button: the screen for its first item still to do.
function nextLink(step) {
  const next = step.items.find((item) => !item.done && ITEM_LINKS[item.key]);
  if (!next) return null;
  const link = ITEM_LINKS[next.key];
  return { to: link.to, label: next.key === 'first_order' ? link.screen : `Open ${link.screen}` };
}

const GettingStarted = ({ onNotification }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStep, setSavingStep] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await onboardingApi.get();
      if (res.success) setData(res.data);
    } catch (err) {
      onNotification?.('Failed to load your setup checklist', 'error');
    } finally {
      setLoading(false);
    }
  }, [onNotification]);

  useEffect(() => { load(); }, [load]);

  const setStep = async (stepKey, status) => {
    setSavingStep(stepKey);
    try {
      const res = await onboardingApi.setStep(stepKey, status);
      if (res.success) await load();
    } catch (err) {
      onNotification?.('Could not update the step — try again', 'error');
    } finally {
      setSavingStep(null);
    }
  };

  // The shell pads every routed page (App.js: px-2 sm:px-4 lg:px-6 py-6); the
  // negative margins let the header band run edge to edge, as on Dashboard.
  const page = (body) => (
    <div className="-mx-2 sm:-mx-4 lg:-mx-6 -mt-6">
      <PageHeader title="Getting started" meta="Set up your shop step by step" />
      <div className="mx-auto flex max-w-3xl flex-col gap-4 px-4 pb-4 pt-5 sm:px-7">{body}</div>
    </div>
  );

  if (loading) {
    return page(<p className="py-12 text-center text-[13px] text-gray-500">Loading your setup checklist…</p>);
  }

  if (!data || !data.applicable) {
    return page(
      <p className="py-12 text-center text-[13px] text-gray-500">
        Setup checklist isn't applicable to this store.
      </p>
    );
  }

  const { steps, progress } = data;
  const pct = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
  // One primary per view: the first step still to do gets it.
  const firstOpenKey = steps.find((step) => step.status !== 'completed' && nextLink(step))?.key;

  return page(
    <>
      <div>
        <p className="text-[13px] text-gray-600">
          Each step ticks itself off once it's set up; skip the optional ones that don't apply yet.
        </p>
        <div className="mt-3">
          <div className="mb-1.5 flex items-center justify-between text-[13px]">
            <span className="font-medium text-gray-900 tabular">{progress.completed} of {progress.total} steps done</span>
            <span className="text-gray-500 tabular">{pct}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-floral-100">
            <div className="h-full rounded-full bg-floral-700 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {progress.done && (
        <div className="banner banner-green">
          <CircleCheckIcon size={16} className="shrink-0" />
          <span>Your shop is fully set up — you're ready to take orders.</span>
        </div>
      )}

      <section className="card">
        <div className="card-header">
          <h2 className="card-title">Setup checklist</h2>
        </div>
        <ol className="divide-y divide-gray-200">
          {steps.map((step) => {
            const link = step.status !== 'completed' ? nextLink(step) : null;
            const done = step.status === 'completed';
            return (
              <li key={step.key} data-testid={`step-${step.key}`} className="px-5 py-4">
                <div className="flex min-w-0 items-start gap-3">
                  {/* Check glyph: ok tone when done, a neutral ring while still to do. */}
                  <div
                    className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular ${
                      done ? 'bg-ok-bg text-ok-fg' : 'border-2 border-gray-300 text-gray-600'
                    }`}
                  >
                    {done ? <CheckIcon size={14} strokeWidth={3} /> : step.order}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-[15px] font-semibold text-gray-900">{step.label}</h3>
                      <span className={`${TAG} ${STATUS_STYLES[step.status] || STATUS_STYLES.pending}`}>
                        {step.status}
                      </span>
                      {!step.skippable && !done && (
                        <span className="text-xs text-gray-600">Needed before you go live</span>
                      )}
                      {step.recommended && !done && (
                        <span className="text-xs font-semibold text-floral-900">Recommended for every shop</span>
                      )}
                    </div>
                    <p className="mt-1 text-[13px] text-gray-600">{step.description}</p>
                    <ul className="mt-2 flex flex-col gap-1">
                      {step.items.map((item) => (
                        <li key={item.key} className="flex items-center gap-2 text-[13px]">
                          {item.done ? (
                            <span className="text-ok-fg" aria-label="Done"><CheckIcon size={16} /></span>
                          ) : (
                            <span className="text-gray-400" aria-label="To do"><CircleIcon size={16} /></span>
                          )}
                          {!item.done && ITEM_LINKS[item.key] ? (
                            <Link to={ITEM_LINKS[item.key].to} className={`font-medium underline underline-offset-2 ${LINK_INK}`}>
                              {item.label}
                            </Link>
                          ) : (
                            <span className={item.done ? 'text-gray-600' : 'text-gray-900'}>{item.label}</span>
                          )}
                        </li>
                      ))}
                    </ul>

                    {(link || (step.skippable && step.status === 'pending') || step.status === 'skipped') && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {link && (
                          <Link
                            to={link.to}
                            className={`${step.key === firstOpenKey ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                          >
                            {link.label}
                          </Link>
                        )}
                        {step.skippable && step.status === 'pending' && (
                          <button
                            onClick={() => setStep(step.key, 'skipped')}
                            disabled={savingStep === step.key}
                            className="btn-ghost btn-sm"
                          >
                            Skip for now
                          </button>
                        )}
                        {step.status === 'skipped' && (
                          <button
                            onClick={() => setStep(step.key, 'pending')}
                            disabled={savingStep === step.key}
                            className="btn-ghost btn-sm"
                          >
                            Undo skip
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    </>
  );
};

export default GettingStarted;
